/**
 * A client space's view of the client organization's compliance program:
 * its policies and procedures, its findings and its record updates, each row
 * marked with what the client's people are shown under the space's client
 * access settings. Reads the client organization's own data through the
 * library's hooks (the session is a member of it: that is what makes it
 * staff of the space), and links into the client organization's own pages.
 *
 * @module components/spaces/SpaceProgram
 */
import { Link } from "@tanstack/react-router";
import { ArrowUpRightIcon, FileTextIcon, ListChecksIcon, SparklesIcon } from "lucide-react";
import { useMemo, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { useRecommendations } from "../../state/governance";
import { UNAVAILABLE_MESSAGES, type OrgProfileUnavailableReason } from "../../state/orgProfile";
import { policyDisplayName, usePolicies } from "../../state/policies";
import type { ClientAccess } from "../../state/spaces";
import { HomeSectionHeader } from "../home/HomeSectionHeader";
import type { FindingItem } from "../regulation/finding/findingItem";
import { useFindingItems } from "../regulation/finding/useFindingItems";
import { ListSkeleton } from "../regulation/LoadingSkeletons";
import { Button } from "../ui/button";
import {
  ageLabel,
  documentVisibility,
  findingMovedMs,
  findingVisibility,
  programDocuments,
  programStats,
  recordUpdateCounts,
  recordUpdatesVisibility,
  summarizeFindings,
  type ProgramDocument,
  type ProgramDocumentKind,
  type ProgramStats,
  type RecordUpdateCounts,
  type Visibility,
  type VisibilityNames,
} from "./SpacePage.logic";

export interface SpaceProgramData {
  readonly ready: boolean;
  readonly unavailableReason: OrgProfileUnavailableReason | null;
  readonly refresh: () => void;
  readonly documents: ReadonlyArray<ProgramDocument>;
  readonly findings: ReadonlyArray<FindingItem>;
  readonly stats: ProgramStats;
  readonly recordUpdates: RecordUpdateCounts;
  readonly kindByDocumentId: ReadonlyMap<string, ProgramDocumentKind>;
}

/** The client organization's program, from the reads its own pages use. Idle without an organization. */
export function useSpaceProgram(clientOrganizationId: string | null): SpaceProgramData {
  const library = usePolicies(clientOrganizationId);
  const findings = useFindingItems(clientOrganizationId);
  // The same cached read `useFindingItems` makes: no second request.
  const suggestions = useRecommendations(clientOrganizationId);
  const documents = useMemo(
    () => programDocuments(library.snapshot?.policies ?? [], policyDisplayName),
    [library.snapshot],
  );
  const stats = useMemo(() => programStats(documents, findings.items), [documents, findings.items]);
  const recordUpdates = useMemo(
    () => recordUpdateCounts(suggestions.snapshot?.recommendations ?? []),
    [suggestions.snapshot],
  );
  const kindByDocumentId = useMemo(
    () => new Map(documents.map((document) => [document.id, document.kind] as const)),
    [documents],
  );
  const refreshLibrary = library.refresh;
  const refreshFindings = findings.refresh;
  return {
    ready: library.snapshot !== null && findings.ready,
    unavailableReason: library.unavailableReason ?? findings.unavailableReason,
    refresh: () => {
      refreshLibrary();
      refreshFindings();
    },
    documents,
    findings: findings.items,
    stats,
    recordUpdates,
    kindByDocumentId,
  };
}

/** "Client sees" in green, "Apex only" in purple; the sentence on hover. */
export function VisibilityBadge({ visibility }: { readonly visibility: Visibility }) {
  return (
    <span
      title={visibility.detail}
      data-space-visibility={visibility.shown ? "shown" : "withheld"}
      className={cn(
        "inline-flex h-5 shrink-0 items-center rounded-full px-2 text-[11px] font-medium leading-none whitespace-nowrap",
        visibility.shown
          ? "bg-emerald-500/10 text-emerald-700 dark:bg-emerald-400/12 dark:text-emerald-300"
          : "bg-violet-500/10 text-violet-700 dark:bg-violet-400/12 dark:text-violet-300",
      )}
    >
      {visibility.label}
    </span>
  );
}

const ROW_CLASS =
  "group flex min-h-9 items-center gap-2.5 rounded-lg px-2 py-1.5 text-[13px] outline-none transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring";

function DocumentMark({ kind }: { readonly kind: ProgramDocumentKind }) {
  const Icon = kind === "policy" ? FileTextIcon : ListChecksIcon;
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-md",
        kind === "policy"
          ? "bg-indigo-500/10 text-indigo-600 dark:text-indigo-300"
          : "bg-teal-500/10 text-teal-700 dark:text-teal-300",
      )}
    >
      <Icon className="size-3.5" />
    </span>
  );
}

export interface ProgramSectionProps {
  readonly program: SpaceProgramData;
  readonly access: ClientAccess;
  readonly names: VisibilityNames;
  /** The client organization's URL segment, for links into its pages. */
  readonly clientSegment: string;
  readonly now: number;
  /** Rows to show; the rest are behind the section's link. */
  readonly limit?: number;
}

export function ProgramDocumentsSection({ program, access, names, clientSegment, now, limit }: ProgramSectionProps) {
  const rows = limit === undefined ? program.documents : program.documents.slice(0, limit);
  const rest = program.documents.length - rows.length;
  return (
    <section aria-label="Policies and procedures" data-space-documents="">
      <HomeSectionHeader
        title="Policies and procedures"
        count={program.documents.length}
        aside={
          <Link
            to="/$org/compliance/policies"
            params={{ org: clientSegment }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            Library
            <ArrowUpRightIcon aria-hidden className="size-3" />
          </Link>
        }
      />
      {rows.length === 0 ? (
        <p className="px-2 py-3 text-[13px] text-muted-foreground">{names.client} has no policies or procedures yet.</p>
      ) : (
        <ul className="-mx-2">
          {rows.map((document) => (
            <li key={document.id}>
              <Link
                to="/$org/compliance/policies/$policyId"
                params={{ org: clientSegment, policyId: document.id }}
                className={ROW_CLASS}
                data-space-document={document.id}
              >
                <DocumentMark kind={document.kind} />
                <span className="min-w-0 flex-1 truncate text-foreground">{document.name}</span>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground max-sm:hidden">
                  v{document.version} · {ageLabel(document.updatedAtMs, now)}
                </span>
                <VisibilityBadge visibility={documentVisibility(access, document.kind, names)} />
              </Link>
            </li>
          ))}
        </ul>
      )}
      {rest > 0 ? (
        <p className="mt-1 px-0.5 text-xs text-muted-foreground">
          {rest} more in the{" "}
          <Link to="/$org/compliance/policies" params={{ org: clientSegment }} className="underline-offset-2 hover:underline">
            library
          </Link>
        </p>
      ) : null}
    </section>
  );
}

function CountPill({ tone, children }: { readonly tone: "open" | "resolved"; readonly children: ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex h-5 items-center rounded-full px-2 text-[11px] font-medium tabular-nums leading-none",
        tone === "open"
          ? "bg-amber-500/12 text-amber-700 dark:text-amber-300"
          : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
      )}
    >
      {children}
    </span>
  );
}

function FindingStatus({ item }: { readonly item: FindingItem }) {
  if (item.status === "open") return <CountPill tone="open">Open</CountPill>;
  const evidence = item.decision?.evidence?.startsWith("File ") ? item.decision.evidence.slice(5) : null;
  return <CountPill tone="resolved">{evidence ? `Resolved ${evidence}` : "Resolved"}</CountPill>;
}

export function ProgramFindingsSection({ program, access, names, clientSegment, now, limit = 8 }: ProgramSectionProps) {
  const summary = useMemo(() => summarizeFindings(program.findings, limit), [limit, program.findings]);
  return (
    <section aria-label="Findings" data-space-findings="">
      <HomeSectionHeader
        title="Findings"
        aside={
          <span className="flex items-center gap-2">
            <CountPill tone="open">{summary.open} open</CountPill>
            <CountPill tone="resolved">{summary.resolved} resolved</CountPill>
            <Link
              to="/$org/compliance/findings"
              params={{ org: clientSegment }}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              All
              <ArrowUpRightIcon aria-hidden className="size-3" />
            </Link>
          </span>
        }
      />
      {summary.latest.length === 0 ? (
        <p className="px-2 py-3 text-[13px] text-muted-foreground">No findings on {names.client}'s documents.</p>
      ) : (
        <ul className="-mx-2">
          {summary.latest.map((item) => (
            <li key={item.id}>
              <Link
                to="/$org/compliance/findings/$findingId"
                params={{ org: clientSegment, findingId: item.id }}
                className={ROW_CLASS}
                data-space-finding={item.id}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-foreground">{item.title}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {item.documentName} · {ageLabel(findingMovedMs(item), now)}
                  </span>
                </span>
                <FindingStatus item={item} />
                <VisibilityBadge
                  visibility={findingVisibility(
                    access,
                    item.policyId === null ? null : (program.kindByDocumentId.get(item.policyId) ?? "policy"),
                    names,
                  )}
                />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function ProgramRecordUpdatesRow({ program, access, names, clientSegment }: ProgramSectionProps) {
  const { pending, applied } = program.recordUpdates;
  return (
    <section aria-label="Suggestions" data-space-record-updates="">
      <HomeSectionHeader title="Suggestions" />
      <div className="-mx-2">
        <Link to="/$org/compliance/suggestions" params={{ org: clientSegment }} className={ROW_CLASS}>
          <span
            aria-hidden
            className="flex size-6 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
          >
            <SparklesIcon className="size-3.5" />
          </span>
          <span className="min-w-0 flex-1 truncate text-foreground">
            {pending === 0 ? "Nothing waiting" : `${pending} waiting for a decision`}
            <span className="text-muted-foreground"> · {applied} applied</span>
          </span>
          <VisibilityBadge visibility={recordUpdatesVisibility(access, names)} />
        </Link>
      </div>
    </section>
  );
}

/** Loading, unavailable, or the program's sections. */
export function ProgramGate({
  program,
  clientName,
  children,
}: {
  readonly program: SpaceProgramData;
  readonly clientName: string;
  readonly children: ReactNode;
}) {
  if (program.unavailableReason !== null) {
    return (
      <div className="rounded-xl border border-dashed border-border/60 px-5 py-6 text-center text-[13px]" data-space-program-unavailable="">
        <p className="font-medium text-foreground">{clientName}'s program could not be loaded</p>
        <p className="mt-1 text-muted-foreground">
          {program.unavailableReason === "error"
            ? "The workspace server did not answer. Try again in a moment."
            : UNAVAILABLE_MESSAGES[program.unavailableReason]}
        </p>
        <Button size="sm" variant="outline" className="mt-3 rounded-full" onClick={program.refresh}>
          Try again
        </Button>
      </div>
    );
  }
  if (!program.ready) return <ListSkeleton rows={5} />;
  return <>{children}</>;
}

